import React, { useState, useEffect } from 'react';
import { candidateAPI } from '../services/api';
import toast from 'react-hot-toast';
import { downloadResumeAuthenticated } from '../utils/resumeUrl';

const JobApplicationModal = ({ job, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    coverLetter: '',
    resumeUrl: '',
    portfolioUrl: '',
    expectedSalary: '',
    noticePeriod: '',
    availabilityDate: '',
    referralSource: '',
  });
  const [loading, setLoading] = useState(false);
  const [resumeUrl, setResumeUrl] = useState('');
  const [noticePeriodType, setNoticePeriodType] = useState(''); // '30', '60', '90', 'negotiate', or ''
  const [customNoticeDays, setCustomNoticeDays] = useState(''); // Custom days input when "Negotiate Days" is selected

  useEffect(() => {
    // Fetch candidate profile to get resume URL
    const fetchProfile = async () => {
      try {
        const response = await candidateAPI.getProfile();
        if (response.data?.resumeUrl) {
          setResumeUrl(response.data.resumeUrl);
          setFormData(prev => ({ ...prev, resumeUrl: response.data.resumeUrl }));
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    };
    fetchProfile();
  }, []);

  // Parse existing notice period value if it exists
  useEffect(() => {
    if (formData.noticePeriod) {
      const noticeValue = formData.noticePeriod.trim();
      // Check if it matches standard values
      if (noticeValue === '30 Days' || noticeValue === '30 days' || noticeValue === '30') {
        setNoticePeriodType('30');
      } else if (noticeValue === '60 Days' || noticeValue === '60 days' || noticeValue === '60') {
        setNoticePeriodType('60');
      } else if (noticeValue === '90 Days' || noticeValue === '90 days' || noticeValue === '90') {
        setNoticePeriodType('90');
      } else {
        // Extract number from string (e.g., "40 Days" -> "40")
        const match = noticeValue.match(/(\d+)/);
        if (match) {
          const days = match[1];
          setNoticePeriodType('negotiate');
          setCustomNoticeDays(days);
        }
      }
    }
  }, []); // Only run once on mount

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNoticePeriodChange = (e) => {
    const value = e.target.value;
    setNoticePeriodType(value);
    
    if (value === 'negotiate') {
      // Clear the notice period, will be set when custom days are entered
      setFormData(prev => ({ ...prev, noticePeriod: '', availabilityDate: '' }));
      setCustomNoticeDays('');
    } else if (value === '30' || value === '60' || value === '90') {
      // Set the notice period with "Days" suffix
      const days = parseInt(value);
      const availabilityDate = calculateAvailabilityDate(days);
      setFormData(prev => ({ ...prev, noticePeriod: `${value} Days`, availabilityDate }));
      setCustomNoticeDays('');
    } else {
      // Empty selection
      setFormData(prev => ({ ...prev, noticePeriod: '', availabilityDate: '' }));
      setCustomNoticeDays('');
    }
  };

  const calculateAvailabilityDate = (days) => {
    const today = new Date();
    const availabilityDate = new Date(today);
    availabilityDate.setDate(today.getDate() + days);
    // Format as YYYY-MM-DD for date input
    return availabilityDate.toISOString().split('T')[0];
  };

  const handleCustomNoticeDaysChange = (e) => {
    const value = e.target.value;
    setCustomNoticeDays(value);
    
    // Only update if it's a valid number
    if (value && !isNaN(value) && parseInt(value) > 0) {
      const days = parseInt(value);
      const availabilityDate = calculateAvailabilityDate(days);
      setFormData(prev => ({ ...prev, noticePeriod: `${value} Days`, availabilityDate }));
    } else if (!value) {
      setFormData(prev => ({ ...prev, noticePeriod: '', availabilityDate: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate notice period is selected
    if (!noticePeriodType || !formData.noticePeriod) {
      toast.error('Please select a notice period');
      return;
    }
    
    setLoading(true);

    try {
      const applicationData = {
        coverLetter: formData.coverLetter || null,
        resumeUrl: formData.resumeUrl || null,
        portfolioUrl: formData.portfolioUrl || null,
        expectedSalary: formData.expectedSalary || null,
        noticePeriod: formData.noticePeriod || null,
        availabilityDate: formData.availabilityDate || null,
        referralSource: formData.referralSource || null,
      };

      const response = await candidateAPI.applyToJob(job.id, applicationData);
      // Pass the job ID to onSuccess so it can update the UI immediately
      onSuccess(job.id);
    } catch (error) {
      console.error('Error applying to job:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to submit application';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop flex items-center justify-center z-50 p-4">
      <div className="modal-panel max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Apply for {job.title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Job Details */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-2">Job Details</h3>
            <p className="text-sm text-gray-600">{job.description || 'No description available'}</p>
            {job.location && (
              <p className="text-sm text-gray-600 mt-1">
                <strong>Location:</strong> {job.location}
              </p>
            )}
            {job.requiredSkills && job.requiredSkills.length > 0 && (
              <div className="mt-2">
                <p className="text-sm font-medium text-gray-700 mb-1">Required Skills:</p>
                <div className="flex flex-wrap gap-2">
                  {job.requiredSkills.map((skill, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-white text-gray-700 text-xs rounded border border-gray-300"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Cover Letter */}
          <div>
            <label htmlFor="coverLetter" className="block text-sm font-medium text-gray-700 mb-2">
              Cover Letter <span className="text-gray-400">(Optional)</span>
            </label>
            <textarea
              id="coverLetter"
              name="coverLetter"
              rows={6}
              value={formData.coverLetter}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Tell us why you're interested in this position..."
            />
          </div>

          {/* Resume */}
          {resumeUrl && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Resume
              </label>
              <div>
                <button
                  type="button"
                  onClick={() =>
                    downloadResumeAuthenticated(resumeUrl).catch(() =>
                      toast.error('Could not download resume')
                    )
                  }
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors text-sm font-medium"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  View Resume
                </button>
              </div>
            </div>
          )}

          {/* Portfolio URL */}
          <div>
            <label htmlFor="portfolioUrl" className="block text-sm font-medium text-gray-700 mb-2">
              Portfolio URL <span className="text-gray-400">(Optional)</span>
            </label>
            <input
              type="url"
              id="portfolioUrl"
              name="portfolioUrl"
              value={formData.portfolioUrl}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="https://..."
            />
          </div>

          {/* Additional Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="expectedSalary" className="block text-sm font-medium text-gray-700 mb-2">
                Expected Salary <span className="text-gray-500 text-xs">(per annum)</span> <span className="text-gray-400">(Optional)</span>
              </label>
              <input
                type="text"
                id="expectedSalary"
                name="expectedSalary"
                value={formData.expectedSalary}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="e.g., $50,000 - $70,000"
              />
            </div>

            <div>
              <label htmlFor="noticePeriod" className="block text-sm font-medium text-gray-700 mb-2">
                Notice Period <span className="text-red-500">*</span>
              </label>
              <select
                id="noticePeriod"
                name="noticePeriod"
                value={noticePeriodType}
                onChange={handleNoticePeriodChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select notice period</option>
                <option value="30">30 Days</option>
                <option value="60">60 Days</option>
                <option value="90">90 Days</option>
                <option value="negotiate">Negotiate Days</option>
              </select>
              
              {/* Show custom input when "Negotiate Days" is selected */}
              {noticePeriodType === 'negotiate' && (
                <div className="mt-2">
                  <input
                    type="number"
                    min="1"
                    id="customNoticeDays"
                    name="customNoticeDays"
                    value={customNoticeDays}
                    onChange={handleCustomNoticeDaysChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter number of days (e.g., 40)"
                  />
                  <p className="mt-1 text-xs text-gray-500">Enter the number of days for your notice period</p>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="availabilityDate" className="block text-sm font-medium text-gray-700 mb-2">
                Joining Date
              </label>
              <input
                type="date"
                id="availabilityDate"
                name="availabilityDate"
                value={formData.availabilityDate}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label htmlFor="referralSource" className="block text-sm font-medium text-gray-700 mb-2">
                How did you hear about us? <span className="text-gray-400">(Optional)</span>
              </label>
              <select
                id="referralSource"
                name="referralSource"
                value={formData.referralSource}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select...</option>
                <option value="LinkedIn">LinkedIn</option>
                <option value="Indeed">Indeed</option>
                <option value="Company Website">Company Website</option>
                <option value="Referral">Referral</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Submit Application'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JobApplicationModal;

